import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';

import { fetchEPDetail } from './helper';

type Props = {
  id: number;
};

const processJsonData = (data: any) => {
  if (!data) return;
  let updated_data = data;

  // scanner_type EC2 to AWS
  if (data['scanner_type'] === 'EC2') {
    updated_data = {
      ...data,
      scanner_type: 'AWS',
    };
  }

  return updated_data;
};

const EPDetailModal = ({ id }: Props) => {
  const [data, setData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEPDetail(id)
      .then((res) => {
        if (res) {
          setData(processJsonData(res));
        }
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const endpointDetailConfig: ConfigItem[] = [
    { name: 'ID', key: 'id', type: 'string' },
    { name: 'Company', key: 'company', type: 'string' },
    { name: 'Qualys Account', key: 'qualys', type: 'string' },
    { name: 'IP', key: 'ip', type: 'string' },
    { name: 'Host ID', key: 'host_id', type: 'string' },
    { name: 'DNS Hostname', key: 'dns', type: 'string' },
    { name: 'FQDN', key: 'fqdn', type: 'string' },
    { name: 'OS', key: 'os', type: 'string' },
    { name: 'Asset Name', key: 'asset_name', type: 'string' },
    { name: 'Alias', key: 'alias', type: 'string' },
    { name: 'Type', key: 'scanner_type', type: 'string' },
    { name: 'Created at', key: 'ca_created', type: 'date' },
    { name: 'Updated at', key: 'ca_modified', type: 'date' },
  ];

  const scanTabConfig: ConfigItem[] = [
    { name: 'Title', key: 'last_scan_title', type: 'string' },
    { name: 'Scanned at', key: 'ca_scanned', type: 'date' },
    { name: 'Status', key: 'last_scan_status', type: 'string' },
    { name: 'Scanner Appliance', key: 'scanner_appliance', type: 'string' },
  ];

  const ec2TabConfig: ConfigItem[] = [
    {
      name: 'Reservation ID',
      key: 'ec2_details.reservation_id',
      type: 'string',
    },
    { name: 'Instance ID', key: 'ec2_details.instance_id', type: 'string' },
    { name: 'Instance Type', key: 'ec2_details.instance_type', type: 'string' },
    {
      name: 'Instance State',
      key: 'ec2_details.instance_state',
      type: 'string',
    },
    {
      name: 'Spot Instance',
      key: 'ec2_details.spot_instance',
      type: 'boolean',
    },
    { name: 'Image (AMI) ID', key: 'ec2_details.image_ami_id', type: 'string' },
    { name: 'Account ID', key: 'ec2_details.account_id', type: 'string' },
    { name: 'Region Name', key: 'ec2_details.region_name', type: 'string' },
    { name: 'Region Code', key: 'ec2_details.region_code', type: 'string' },
    {
      name: 'Availability Zone',
      key: 'ec2_details.availability_zone',
      type: 'string',
    },
    { name: 'Subnet ID', key: 'ec2_details.subnet_id', type: 'string' },
    { name: 'VPC ID', key: 'ec2_details.vpc_id', type: 'string' },
    { name: 'DNS (Private)', key: 'ec2_details.dns_private', type: 'string' },
    { name: 'DNS (Public)', key: 'ec2_details.dns_public', type: 'string' },
    {
      name: 'Local Hostname',
      key: 'ec2_details.local_hostname',
      type: 'string',
    },
    { name: 'MAC Address', key: 'ec2_details.mac_address', type: 'string' },
    {
      name: 'IP Address (Private)',
      key: 'ec2_details.ip_address_private',
      type: 'string',
    },
    {
      name: 'IP Address (Public)',
      key: 'ec2_details.ip_address_public',
      type: 'string',
    },
    { name: 'Group ID', key: 'ec2_details.group_id', type: 'string' },
    { name: 'Group Name', key: 'ec2_details.group_name', type: 'string' },
    {
      name: 'Tags',
      key: 'ec2_details.tags',
      type: 'object',
      config: [{ name: 'Name', key: 'Name', type: 'string' }],
    },
  ];

  // tabs is the configuration for the tabs which are rendered
  const tabs = [
    {
      title: 'Details',
      config: endpointDetailConfig,
      data: data,
    },
    {
      title: 'Last Scan',
      config: scanTabConfig,
      data: data,
    },
    {
      title: 'AWS Information',
      config: ec2TabConfig,
      data: data,
    },
  ];

  if (!data) return <LoadingScreen />;

  return (
    <CustomModalWrapper id="endpoint-scroll">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                      display:
                        tab.title === 'AWS Information' &&
                        [null, 'IP'].includes(tab.data.ep_tracking_method)
                          ? 'none'
                          : 'block',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col sm="12" md="8" lg="9">
              {tabs.map(
                (tab, index) =>
                  activeTab === index && (
                    <DetailTable
                      key={index}
                      data={tab.data}
                      config={tab.config}
                    />
                  ),
              )}
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default EPDetailModal;
